/**
 * @unscoped — Drift opt-out 안내 메시지 i18n — Cycle 26 → 41.
 *
 * Cycle 41 — base-i18n.ts 통합 helper 사용. lang state / storage / listener 는 base 가 단일 source.
 * 본 모듈은 MESSAGES 사전 + t 래퍼만 유지. setDriftLang/subscribeDriftLang 은 base 의 thin wrapper.
 *
 * (cycle 26-40 history: navigator detect, storage key 공유, globalThis instance)
 *
 * Cycle 47 — ja key 보강 + fallback chain 정책 (lang → en → ko) 명시화.
 *   기존: m[lang] ?? m[ko] — partial 정의 키에서 ja → ko 직행 (불일치 강도 큼)
 *   개선: m[lang] ?? m.en ?? m[ko] — antipattern-i18n-third-lang-cascading-fallback-without-explicit-fallback-chain
 *   학습 phase 5 정책 적용. base-i18n + admin-i18n 와 동일 체인.
 */
import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
  _setSharedLangForTests, _getSharedLangForTests,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  'drift.section.heading': { ko: '알림 설정', en: 'Notification Settings', ja: '通知設定' },
  'drift.section.intro': {
    ko: 'LLM 사용량/요금 drift 이메일은 관리자(ADMIN) 의 외부 inbox 로 자동 발송됩니다. 본인이 수신을 거부하면 본인 inbox 만 제외되며, 운영팀(DRIFT_EMAIL_TO) 은 계속 수신합니다.',
    en: 'LLM usage/billing drift emails are sent automatically to administrator (ADMIN) external inboxes. If you opt out, only your inbox is excluded; the operations team (DRIFT_EMAIL_TO) continues to receive them.',
    ja: 'LLM使用量/料金のドリフトメールは管理者(ADMIN)の外部受信箱に自動送信されます。個人で受信を拒否すると個人の受信箱のみ除外され、運用チーム(DRIFT_EMAIL_TO)は引き続き受信します。',
  },
  'drift.toggle.personal': {
    ko: '본인 drift 이메일 수신 거부',
    en: 'Opt out of drift emails (personal)',
    ja: 'ドリフトメールの受信を拒否(個人)',
  },
  'drift.toggle.institution': {
    ko: '회사 전체 drift 이메일 수신 거부',
    en: 'Block drift emails for the entire company',
    ja: '会社全体のドリフトメール受信を拒否',
  },
  'drift.reason.label': { ko: '사유 (선택)', en: 'Reason (optional)', ja: '理由(任意)' },
  'drift.reason.placeholder': {
    ko: '예: 운영팀 채널에서 수신 충분',
    en: 'e.g., already covered by ops Slack channel',
    ja: '例: 運用チームチャネルで受信済み',
  },
  'drift.save_hint': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'drift.pii.warning': {
    ko: '개인정보로 의심되는 패턴이 포함되어 있습니다',
    en: 'The reason contains patterns that look like personal information',
    ja: '個人情報の可能性があるパターンが含まれています',
  },
  'drift.pii.audit_note': {
    ko: '저장은 가능하지만 감사 로그에는 마스킹되어 기록됩니다.',
    en: 'Save is allowed, but the audit log records the masked version.',
    ja: '保存は可能ですが、監査ログにはマスクされた値が記録されます。',
  },
  'drift.pii.kind.email': { ko: '이메일', en: 'email', ja: 'メール' },
  'drift.pii.kind.phone': { ko: '전화번호', en: 'phone', ja: '電話番号' },
  'drift.pii.kind.rrn': { ko: '주민번호', en: 'resident ID', ja: '住民登録番号' },
  'drift.server_pii_block': {
    ko: '사유에 개인정보 패턴이 감지되어 저장이 차단되었습니다',
    en: 'Save blocked because the reason contains personal information patterns',
    ja: '理由に個人情報パターンが検出されたため保存がブロックされました',
  },
  'drift.server_pii_hint': {
    ko: '사유를 일반 텍스트로 수정해 주세요.',
    en: 'Please rewrite the reason as plain text.',
    ja: '理由をプレーンテキストに修正してください。',
  },
  'drift.confirm.institution_on': {
    ko: 'institution 전체 ADMIN 의 drift email 수신을 끄시겠습니까? 다른 관리자에게도 영향이 갑니다.',
    en: 'Disable drift emails for all administrators in this institution? Other administrators will be affected.',
    ja: '会社全体のADMINのドリフトメール受信をオフにしますか? 他の管理者にも影響します。',
  },
  'drift.since_label': { ko: '수신 거부 시작:', en: 'Opted out since:', ja: '受信拒否の開始:' },
  'drift.updated_at_label': { ko: '사유 마지막 변경:', en: 'Reason last updated:', ja: '理由の最終変更:' },
  // Cycle 27 — 잔여 i18n
  'drift.loading': { ko: '불러오는 중...', en: 'Loading...', ja: '読み込み中...' },
  'drift.section.institution_heading': { ko: '회사 전체 수신 정책', en: 'Institution-wide policy', ja: '会社全体の受信ポリシー' },
  'drift.section.institution_intro': {
    ko: '본 옵션을 켜면 회사의 모든 관리자(ADMIN) 의 drift email 수신이 중단됩니다.',
    en: 'Enabling this stops drift emails for all administrators (ADMIN) of this company.',
    ja: 'このオプションをオンにすると、会社のすべての管理者(ADMIN)のドリフトメール受信が停止されます。',
  },
  'drift.section.institution_warning': {
    ko: '다른 관리자에게도 영향',
    en: 'Other administrators are affected',
    ja: '他の管理者にも影響',
  },
  'drift.section.institution_disclaimer': {
    ko: '이 가므로 사전 협의 후 사용해 주세요.',
    en: '— please coordinate before using.',
    ja: 'があるため、事前に協議してください。',
  },
  'drift.section.personal_heading': { ko: '본인 수신 설정', en: 'Personal setting', ja: '個人受信設定' },
  'drift.reason_save_button': { ko: '사유 저장', en: 'Save reason', ja: '理由を保存' },
  'drift.save_failed': { ko: '저장 실패', en: 'Save failed', ja: '保存失敗' },
  'drift.fetch_failed': { ko: '알림 설정 불러오기 실패', en: 'Failed to load notification settings', ja: '通知設定の読み込みに失敗' },
  // Cycle 28 — DriftOptOutDashboard 의 message
  'driftdash.heading': { ko: 'Drift 이메일 수신 거부 현황', en: 'Drift Email Opt-out Overview', ja: 'ドリフトメール受信拒否状況' },
  'driftdash.subheading': {
    ko: 'institution-wide 차단 (회사 전체 ADMIN inbox 차단) + 본인 수신 거부 (관리자 개인) 통계. 개별 사용자 row 는 노출하지 않습니다 (privacy).',
    en: 'Institution-wide blocks (entire company ADMIN inbox) + personal opt-out (individual administrators) statistics. Individual user rows are not exposed (privacy).',
    ja: '会社全体のブロック(会社全体のADMIN受信箱ブロック) + 個人受信拒否(管理者個人)の統計。個別ユーザー行は表示しません(プライバシー)。',
  },
  'driftdash.fetch_failed': { ko: 'drift opt-out 데이터 불러오기 실패', en: 'Failed to load drift opt-out data', ja: 'ドリフトオプトアウトデータの読み込みに失敗' },
  'driftdash.summary.inst_disabled': { ko: '회사 전체 차단', en: 'Companies blocked', ja: '会社全体ブロック' },
  'driftdash.summary.user_total': { ko: '개인 수신 거부 (전체)', en: 'Personal opt-outs (total)', ja: '個人受信拒否(全体)' },
  'driftdash.chart.heading': { ko: '최근 30일 변경 분포', en: 'Last 30 days change distribution', ja: '最近30日間の変更分布' },
  'driftdash.history.heading': { ko: '최근 변경 이력', en: 'Recent changes', ja: '最近の変更履歴' },
  'driftdash.history.empty': { ko: '변경 이력이 없습니다.', en: 'No change history.', ja: '変更履歴がありません。' },
  'driftdash.inst_list.heading': { ko: '회사 전체 차단', en: 'Institution-wide blocks', ja: '会社全体ブロック' },
  'driftdash.inst_list.empty': { ko: '차단된 회사가 없습니다.', en: 'No companies blocked.', ja: 'ブロックされた会社はありません。' },
  'driftdash.user_stats.heading': { ko: '회사별 개인 수신 거부 통계', en: 'Personal opt-out stats by company', ja: '会社別個人受信拒否統計' },
  'driftdash.user_stats.empty': { ko: '개인 수신 거부 사용자가 없습니다.', en: 'No personal opt-out users.', ja: '個人受信拒否ユーザーがいません。' },
  'driftdash.col.company': { ko: '회사명', en: 'Company', ja: '会社名' },
  'driftdash.col.domain': { ko: '도메인', en: 'Domain', ja: 'ドメイン' },
  'driftdash.col.detail': { ko: '상세', en: 'Detail', ja: '詳細' },
  'driftdash.col.count': { ko: '수신 거부 사용자 수', en: 'Opt-out users', ja: '受信拒否ユーザー数' },
  'driftdash.col.latest_change': { ko: '최근 변경', en: 'Latest change', ja: '最近の変更' },
  'driftdash.col.time': { ko: '시각', en: 'Time', ja: '時刻' },
  'driftdash.col.scope': { ko: '범위', en: 'Scope', ja: '範囲' },
  'driftdash.col.action': { ko: '액션', en: 'Action', ja: 'アクション' },
  'driftdash.col.actor': { ko: 'actor', en: 'Actor', ja: 'アクター' },
  'driftdash.col.reason_masked': { ko: '사유 (마스킹)', en: 'Reason (masked)', ja: '理由(マスク)' },
  'driftdash.back_link': { ko: '← 테넌트 목록', en: '← Tenant list', ja: '← テナント一覧' },
  'driftdash.tenant_link': { ko: 'tenant 보기', en: 'View tenant', ja: 'テナントを表示' },
  'driftdash.scope.user': { ko: '본인', en: 'Personal', ja: '個人' },
  'driftdash.scope.institution': { ko: '회사', en: 'Institution', ja: '会社' },
  'driftdash.action.opt_out': { ko: '본인 거부', en: 'Opt out', ja: '個人拒否' },
  'driftdash.action.opt_in': { ko: '본인 해제', en: 'Opt in', ja: '個人解除' },
  'driftdash.action.inst_opt_out': { ko: '회사 차단', en: 'Inst opt out', ja: '会社ブロック' },
  'driftdash.action.inst_opt_in': { ko: '회사 해제', en: 'Inst opt in', ja: '会社解除' },
  'driftdash.legend.opt_out': { ko: '본인 거부', en: 'Opt out', ja: '個人拒否' },
  'driftdash.legend.opt_in': { ko: '본인 해제', en: 'Opt in', ja: '個人解除' },
  'driftdash.legend.inst_opt_out': { ko: '회사 차단', en: 'Inst opt out', ja: '会社ブロック' },
  'driftdash.legend.inst_opt_in': { ko: '회사 해제', en: 'Inst opt in', ja: '会社解除' },
  'driftdash.access_denied': { ko: '접근 권한이 없습니다', en: 'Access denied', ja: 'アクセス権限がありません' },
  // Cycle 34 — cumulative trend chart
  'driftdash.cumchart.heading': { ko: '최근 30일 누적 추세', en: 'Last 30 days cumulative trend', ja: '最近30日間の累積トレンド' },
  'driftdash.cumchart.personal_net': { ko: '본인 net (opt_out - opt_in)', en: 'Personal net', ja: '個人 net (opt_out - opt_in)' },
  'driftdash.cumchart.institution_net': { ko: '회사 net (차단 - 해제)', en: 'Institution net', ja: '会社 net (ブロック - 解除)' },
};

// Cycle 41 — base-i18n.ts 의 단일 globalThis state 사용. 자체 state 제거.
// 모든 lang read/write 가 base 위임. admin-i18n 와 동일 source of truth.

export function t(key: keyof typeof MESSAGES): string {
  const m = MESSAGES[key];
  if (!m) return key;
  // Cycle 47 — fallback chain (cycle 46 학습 phase 5 정책):
  //   lang → en → FALLBACK_LANG (ko). third lang 부분 정의 시 en 으로 안전 회수.
  //   cycle 47 에 64 객체 모두 ja key 추가됐으나 chain 명시로 향후 새 lang 추가 시에도 안전.
  return m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG];
}

export function getDriftLang(): Lang {
  return getSharedLang();
}

export function setDriftLang(value: Lang): void {
  setSharedLang(value);
}

export function subscribeDriftLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}

export function _setLangForTests(value: Lang): void {
  _setSharedLangForTests(value);
}

export function _getCurrentLangForTests(): Lang {
  return _getSharedLangForTests();
}
